import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/use-debounce';

interface UsernameValidationResult {
  isValid: boolean;
  isAvailable: boolean;
  isReserved: boolean;
  canChange: boolean;
  nextChangeDate: Date | null;
  isLoading: boolean;
  error: string | null;
  suggestion: string | null;
}

export function useUsernameValidation(username: string, userId?: string) {
  const [result, setResult] = useState<UsernameValidationResult>({
    isValid: false,
    isAvailable: false,
    isReserved: false,
    canChange: true,
    nextChangeDate: null,
    isLoading: false,
    error: null,
    suggestion: null,
  });

  const debouncedUsername = useDebounce(username, 500);

  useEffect(() => {
    if (!debouncedUsername || debouncedUsername.length < 3) {
      setResult(prev => ({ 
        ...prev, 
        isValid: false, 
        isLoading: false,
        error: debouncedUsername.length > 0 ? 'Username must be at least 3 characters' : null
      }));
      return;
    }

    validateUsername(debouncedUsername, userId);
  }, [debouncedUsername, userId]);

  const validateUsername = async (usernameToCheck: string, userIdToCheck?: string) => {
    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Check basic format
      const isValidFormat = /^[a-zA-Z0-9_]+$/.test(usernameToCheck);
      if (!isValidFormat) {
        setResult(prev => ({
          ...prev,
          isValid: false,
          isLoading: false,
          error: 'Username can only contain letters, numbers, and underscores'
        }));
        return;
      }

      // Check if user can change username (rate limiting)
      let canChange = true;
      let nextChangeDate = null;
      if (userIdToCheck) {
        const { data: changeData, error: changeError } = await supabase
          .rpc('can_change_username', { user_id_param: userIdToCheck });
        
        if (changeError) throw changeError;
        canChange = changeData;

        if (!canChange) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('username_last_changed')
            .eq('user_id', userIdToCheck)
            .single();
          
          if (profileData?.username_last_changed) {
            nextChangeDate = new Date(new Date(profileData.username_last_changed).getTime() + 30 * 24 * 60 * 60 * 1000);
          }
        }
      }

      // Check if username is reserved
      const { data: reservedData, error: reservedError } = await supabase
        .rpc('is_username_reserved', { username_param: usernameToCheck });
      
      if (reservedError) throw reservedError;
      const isReserved = reservedData;

      if (isReserved) {
        setResult(prev => ({
          ...prev,
          isValid: false,
          isReserved: true,
          canChange,
          nextChangeDate,
          isLoading: false,
          error: 'This username is reserved'
        }));
        return;
      }

      // Check if username is available
      const { data: existingData, error: existingError } = await supabase
        .from('profiles')
        .select('username, user_id')
        .ilike('username', usernameToCheck)
        .limit(1);

      if (existingError) throw existingError;

      const isAvailable = !existingData || existingData.length === 0 || 
        (userIdToCheck && existingData[0]?.user_id === userIdToCheck);

      let suggestion = null;
      if (!isAvailable && canChange) {
        // Generate suggestion using the existing function
        const { data: suggestionData, error: suggestionError } = await supabase
          .rpc('generate_unique_username', { base_username: usernameToCheck });
        
        if (!suggestionError && suggestionData) {
          suggestion = suggestionData;
        }
      }

      setResult({
        isValid: isAvailable && canChange && !isReserved,
        isAvailable,
        isReserved,
        canChange,
        nextChangeDate,
        isLoading: false,
        error: !isAvailable ? 'Username is already taken' : 
               !canChange ? 'You can only change your username once every 30 days' : null,
        suggestion,
      });

    } catch (error) {
      console.error('Username validation error:', error);
      setResult(prev => ({
        ...prev,
        isValid: false,
        isLoading: false,
        error: 'Error validating username'
      }));
    }
  };

  return result;
}